<template>
    <app-layout title="Customers">
        <page-header>
            <template #title>Customers</template>

            <template #actions>
                <jet-dropdown align="right" width="24">
                    <template #trigger>
                        <jet-tertiary-button size="small" class="py-1 h-8">
                            <img src="/images/icons/excel.svg" width="14" height="14" class="mx-auto" />
                            <img class="ml-2" src="/images/icons/chevron-down.svg" width="16" height="16" alt="Export">
                        </jet-tertiary-button>
                    </template>
                    <template #content>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer whitespace-nowrap" @click="exportCustomers('all')">All Customers</div>
                        <div class="px-4 py-2 hover:bg-gray cursor-pointer whitespace-nowrap" @click="exportCustomers('filtered')">Filtered Customers</div>
                    </template>
                </jet-dropdown>
                <jet-button type="button" class="" :href="route('customers.create')" :disabled="form.processing" >
                    Add
                </jet-button>
            </template>
        </page-header>

        <div class="px-8">
            <div class="flex">
                <form class="flex gap-2 flex-wrap lg:w-full" @submit.prevent="handleSubmit">
                    <!-- Filters in column order: BC, Name, Chain, Route, City -->
                    <div class="flex flex-1 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="search" value="Search" />
                            <jet-input type="text" class="h-7" name="search" v-model="form.search" @input="handleSubmit" placeholder="Search..."/>
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="bc_status" value="BC Status" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.bc_status"
                                :options="bcStatusOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="chain" value="Chain" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.chain"
                                :options="chainOptions"
                                valueProp="value"
                                trackBy="value"
                                label="label"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="route" value="Route" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.route"
                                :options="routeOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="city" value="City" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.city"
                                :options="cityOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                    </div>
                    <!-- Type, Pricing Level, Sales Rep, Last Order Date Range, Status -->
                    <div class="flex flex-1 gap-x-2">
                        <div class="flex flex-col flex-1">
                            <jet-label for="type" value="Type" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.type"
                                :options="typeOptions"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="pricing_level" value="Pricing Level" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.pricing_level"
                                :options="pricingOptions"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="onboarding_status" value="Onboarding" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.onboarding_status"
                                :options="onboardingStatusOptions"
                                valueProp="value"
                                trackBy="value"
                                label="label"
                                placeholder="All Statuses"
                                :classes="{singleLabel: 'w-full px-2'}"
                                @change="handleSubmit"
                            />
                        </div>

                        <div class="flex flex-col flex-1">
                            <jet-label for="sales_rep" value="Sales Rep" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.sales_rep"
                                :options="repOptions"
                                valueProp="id"
                                trackBy="id"
                                label="name"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1">
                            <jet-label for="status" value="Status" />
                            <multiselect class="multi-sm multi-custom h-7"
                                v-model="form.status"
                                :options="statusFilterOptions"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="All"
                                :searchable="true"
                                @change="handleSubmit"
                            />
                        </div>
                        <div class="flex flex-col flex-1 min-w-48">
                            <jet-label for="date_range" value="Last Order Date Range" />
                            <datepicker inputClassName="dp-custom-input"
                                @update:modelValue="handleSubmit"
                                v-model="date"
                                :is24="false"
                                :enableTimePicker="false"
                                :autoApply="true"
                                range
                                twoCalendars
                                placeholder="Select Date Range"
                                :format="formatDateRange"
                                weekStart="0"
                            />
                        </div>
                    </div>
                </form>
                <div class="self-end">
                    <jet-tertiary-button type="button" class="py-1 ml-4 h-7" @click="handleReset" >
                        Reset
                    </jet-tertiary-button>
                </div>
            </div>
        </div>

        <div class="px-8 mt-2">
            <div class="rounded-t overflow-hidden border border-b-0">
                <table class="table table-with-links w-full">
                    <thead>
                        <tr>
                            <th class="text-center w-0">
                                <jet-input type="checkbox" class="h-5 w-5" @click="toggleEditAll()" :checked="allSelected"></jet-input>
                            </th>
                            <th class="text-left">BC</th>
                            <th class="text-left">Name</th>
                            <th class="text-left">Chain</th>
                            <th class="text-left">Route</th>
                            <th class="text-left">City</th>
                            <th class="text-left">Sales Rep</th>
                            <th class="text-left">Status</th>
                            <th class="text-left">Total Orders</th>
                            <th class="text-left">Last Order</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(customer,index) in customers.data" :key="customer.id">
                            <td class="text-center">
                                <jet-input type="checkbox" class="h-5 w-5" @change="toggleEdit(customer)" :checked="displayedCustomers[index] ? displayedCustomers[index].edit : false"></jet-input>
                            </td>
                            <td>
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    <img src="/images/icons/green_check.svg" v-if="BCInfoFilled(customer) == 'no_error'" width="24" height="24" class="mx-auto" />
                                    <img src="/images/icons/x-red.svg" v-if="BCInfoFilled(customer) == 'error'" width="24" height="24" class="mx-auto" :title="errorMessageBC(customer.bc_error)" />
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer.name }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer?.chain?.name }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer.route }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer.city }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer.sales_rep?.name }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="customerHref(customer)">
                                    {{ customer.enabled === true ? 'Active' : 'Inactive' }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="route('customers.edit', customer.id)">
                                    {{ customer.order_count }}
                                </Link>
                            </td>
                            <td class="text-left">
                                <Link class="block px-2 py-1 h-full" :href="route('customers.edit', customer.id)">
                                    <span v-if="customer.next_order_to_ship != null">
                                        {{ formatDate(customer.next_order_to_ship) }}
                                    </span>
                                    <span v-else-if="customer.last_order_shipped != null">
                                        {{ formatDate(customer.last_order_shipped) }}
                                    </span>
                                    <span v-else>
                                        No orders
                                    </span>
                                </Link>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex justify-between items-center mt-4">
                <div v-show="customersToEdit.length">
                    <jet-secondary-button class="" @click="showEditModal = true" :disabled="customersToEdit.length == 0">
                        Edit ({{ customersToEdit.length }} selected)
                    </jet-secondary-button>
                </div>

                <pagination class="mt-0 mb-0" :links="customers.links" />

                <div class="w-40">
                    <multiselect
                        v-model.number="form.paginate"
                        :options="paginateOptions"
                        :canDeselect="false"
                        :canClear="false"
                        :searchable="true"
                        class="h-8 multi-sm multi-custom text-base"
                        openDirection="top"
                        @change="handleSubmit"
                    />
                </div>
            </div>
        </div>

        <jet-dialog-modal :show="showEditModal" @close="handleResetEdit">
            <template #title>
                <h2>Customer Bulk Edit</h2>
            </template>

            <template #content>
                <form @submit.prevent="handleBulkEdit">
                    <div class="flex flex-col gap-y-2 px-8 py-4 h-80">
                        <div class="flex">
                            <div style="min-width:8rem">
                                <jet-label for="enabled" class="mt-1" value="Status:"/>
                            </div>
                            <div class="flex flex-col flex-1">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="formEdit.enabled"
                                    :options="editStatusOptions"
                                    placeholder="Select a status"
                                    :searchable="true"
                                />
                                <jet-input-error :message="$page.props.errors.enabled" class="mt-1" />
                            </div>
                        </div>


                        <div class="flex">
                            <div style="min-width:8rem">
                                <jet-label for="type" class="mt-1" value="Type:"/>
                            </div>
                            <div class="flex flex-col flex-1">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="formEdit.type"
                                    :options="typeOptions"
                                    placeholder="Select a type"
                                    :searchable="true"
                                />
                                <jet-input-error :message="$page.props.errors.type" class="mt-1" />
                            </div>
                        </div>

                        <div class="flex">
                            <div style="min-width:8rem">
                                <jet-label for="pricing_level" class="mt-1" value="Pricing Level:"/>
                            </div>
                            <div class="flex flex-col flex-1">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="formEdit.pricing_level_id"
                                    :options="pricingOptions"
                                    placeholder="Select a pricing level"
                                    :searchable="true"
                                />
                            </div>
                        </div>

                        <div class="flex">
                            <div style="min-width:8rem">
                                <jet-label for="route" class="mt-1" value="Route:"/>
                            </div>
                            <div class="flex flex-col flex-1">
                                <multiselect class="multi-sm h-7 text-base"
                                    v-model="formEdit.route"
                                    :options="routeOptions"
                                    valueProp="value"
                                    trackBy="label"
                                    label="label"
                                    placeholder="Select a route"
                                    :searchable="true"
                                />
                                <jet-input-error :message="$page.props.errors.route" class="mt-1" />
                            </div>
                        </div>
                    </div>
                    <jet-button type="submit" class="hidden" ref="formEditBtn"/>
                </form>
            </template>

            <template #footer>
                <jet-button class="mr-2" @click.prevent="$refs.formEditBtn.$el.click()"
                    :class="{ 'opacity-25': formEdit.processing }"
                    :disabled="formEdit.processing || !formEdit.isDirty">
                    Save
                </jet-button>

                <jet-tertiary-button @click="handleResetEdit">
                    Cancel
                </jet-tertiary-button>
            </template>
        </jet-dialog-modal>
    </app-layout>
</template>

<script>
import { useForm, Link } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetDialogModal from '@/Jetstream/DialogModal';
import JetDropdown from '@/Jetstream/Dropdown';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import Pagination from '@/Jetstream/Pagination'
import Multiselect from '@vueform/multiselect';
import Datepicker from '@vuepic/vue-datepicker';
import { DateTime } from "luxon";

import '@vueform/multiselect/themes/default.css';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
    setup(props) {
        const form = useForm({
            bc_status: props.filters.bc_status,
            search: props.filters.search,
            chain: props.filters.chain != null && props.filters.chain !== '' ? Number(props.filters.chain) : null,
            route: props.filters.route,
            city: props.filters.city,
            status: props.filters.status,
            last_order_from: props.filters.last_order_from,
            last_order_to: props.filters.last_order_to,
            type: props.filters.type,
            pricing_level: props.filters.pricing_level,
            sales_rep: props.filters.sales_rep,
            onboarding_status: props.filters.onboarding_status ?? null,
            paginate: props.paginate,
        });
        const formEdit = useForm({
            enabled: null,
            type: null,
            pricing_level_id: null,
            route: null,
        });

        return { form, formEdit }
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetDialogModal,
        JetDropdown,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        Link,
        Multiselect,
        Pagination,
        Datepicker,
    },

    props: ['customers', 'filters', 'paginate', 'typeOptions', 'pricingOptions', 'routeOptions', 'repOptions', 'deliveryOptions', 'chainOptions', 'cityOptions', 'errors'],

    data() {
        return {
            showEditModal: false,
            displayedCustomers: [],
            date: null,

            paginateOptions: [
                { value: 20, label:'Show 20'},
                { value: 50, label:'Show 50'},
                { value: 100, label:'Show 100'},
            ],

            bcStatusOptions: [
                { value: '', label: 'All' },
                { value: 'synced', label: 'Synced' },
                { value: 'not_synced', label: 'Not Synced' },
            ],

            statusFilterOptions: [
                { value: '', label: 'All' },
                { value: 1, label: 'Active' },
                { value: 0, label: 'Inactive' },
            ],

            editStatusOptions: [
                { value: 1, label: 'Active' },
                { value: 0, label: 'Inactive' },
            ],

            onboardingStatusOptions: [
                { value: null, label: 'All Statuses' },
                { value: 'invited', label: 'Invited' },
                { value: 'awaiting_business_review', label: 'Awaiting business review' },
                { value: 'correction_required_business', label: 'Correction required (business)' },
                { value: 'awaiting_payment_review', label: 'Awaiting payment review' },
                { value: 'correction_required_payment', label: 'Correction required (payment)' },
                { value: 'awaiting_activation', label: 'Awaiting Activation' },
                { value: 'activated', label: 'Activated' },
                { value: 'rejected', label: 'Rejected' },
                { value: 'awaiting_exception_review', label: 'Awaiting exception review' },
                { value: 'exception_accepted', label: 'Exception accepted / skipping payment' },
            ],
        }
    },

    methods: {
        handleSubmit: _.debounce(function () {
            // Debounced handler for customer filters/search (uses Lodash debounce).
            this.form
                .transform((data) => {
                    const base = {
                        // Customer filter fields.
                        bc_status: data.bc_status ?? '',
                        search: data.search ?? '',
                        chain: data.chain ?? '',
                        route: data.route ?? '',
                        city: data.city ?? '',
                        status: data.status !== undefined && data.status !== null ? data.status : '',
                        last_order_from: this.date ? DateTime.fromJSDate(this.date[0]).toISODate() : null,
                        last_order_to: this.date ? DateTime.fromJSDate(this.date[1]).toISODate() : null,
                        type: data.type ?? '',
                        pricing_level: data.pricing_level ?? '',
                        onboarding_status: data.onboarding_status ?? '',
                        sales_rep: data.sales_rep ?? '',
                        paginate: data.paginate ?? 20,
                    };
                    return base;
                })
                .get(route('customers.index'), { preserveState: true });
        }, 250),

        handleReset () {
            // Clear filter fields
            this.date = null;
            const query = {
                bc_status: '',
                search: '',
                chain: '',
                route: '',
                city: '',
                status: '',
                last_order_from: '',
                last_order_to: '',
                type: '',
                pricing_level: '',
                onboarding_status: '',
                sales_rep: '',
                paginate: this.form.paginate,
            };
            // Reload customers with cleared filters, resetting state
            Inertia.get(route('customers.index'), query, { preserveState: false });
        },

        setDateRange() {
            // Initialize date picker from filter values, if set
            if (this.filters.last_order_from != null && this.filters.last_order_to != null) {
                this.date = [
                    this.formatDateToJS(this.filters.last_order_from),
                    this.formatDateToJS(this.filters.last_order_to),
                ];
            }
        },

        formatDateToJS(date) {
            // Convert an ISO date string to JS Date object
            return DateTime.fromISO(date).toJSDate();
        },

        formatDateRange(dateRange) {
            // Produces a human friendly range string: "Jan. 1 - Jan. 10" or with year when needed
            if (!dateRange || !dateRange[0] || !dateRange[1]) return null;
            const start = dateRange[0];
            const end = dateRange[1];
            const thisYear = DateTime.now().toFormat('yyyy');
            const startYear = DateTime.fromJSDate(start).toFormat('yyyy');
            const endYear = DateTime.fromJSDate(end).toFormat('yyyy');
            const startDate = startYear === thisYear
                ? DateTime.fromJSDate(start).toFormat('LLL. d')
                : DateTime.fromJSDate(start).toFormat('LLL. d, yyyy');
            const endDate = endYear === thisYear
                ? DateTime.fromJSDate(end).toFormat('LLL. d')
                : DateTime.fromJSDate(end).toFormat('LLL. d, yyyy');
            return startDate + ' - ' + endDate;
        },

        handleResetEdit () {
            this.formEdit.reset();
            this.displayedCustomers.forEach(i => i.edit = false);
            this.showEditModal = false;
        },

        handleBulkEdit () {
            let editCustomers = this.customersToEdit.map(i => i.id);

            this.formEdit.transform((data) => ({
                ...data,
                customers: editCustomers,
            })).post(route('customers.bulk'), {
                onSuccess: () => {
                    this.customersToEdit.forEach(i => i.edit = false);
                    this.formEdit.reset();
                    this.showEditModal = false;
                },
                onError: (errors) => console.log(errors),
            });
        },

        toggleEdit (customer) {
            let dispCust = this.displayedCustomers.find(i => i.id === customer.id);
            dispCust.edit = !dispCust.edit;
        },

        toggleEditAll () {
            let update = !this.allSelected;
            this.displayedCustomers.forEach(i => i.edit = update);
        },

        formatDate (date) {
            return DateTime.fromISO(date).toFormat('MMM d, yyyy');
        },

        BCInfoFilled (customer) {
            if (customer.bc_error != null)
                return 'error';
            else if (customer.bc_id != null && customer.bc_error == null)
                return 'no_error';
            else 
                return false;
        },

        errorMessageBC(errorString) {
            try {
                // Check if the error string is XML
                if (errorString.startsWith('<error')) {
                    // Parse XML string
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(errorString, 'application/xml');
                    const code = xmlDoc.getElementsByTagName('code')[0]?.textContent;
                    const message = xmlDoc.getElementsByTagName('message')[0]?.textContent;
                    if (code && message) {
                        return `${code}. ${message}`;
                    }
                } else {
                    // Parse JSON string
                    const errorObj = JSON.parse(errorString);
                    if (errorObj.error && errorObj.error.message) {
                        return errorObj.error.message;
                    }
                }
            } catch (e) {
                console.error('Error parsing the error string:', e);
            }

            return 'An unknown error occurred';
        },

        exportCustomers(type) {
            const url = route('customers.excel_export', { type: type });
            window.open(url, '_blank');
        },

        customerHref(customer) {
        // if the customer isn't activated, go to the setup page instead of edit
        return customer.onboarding_status == 'activated'
            ? route('customers.edit', customer.id)
            : route('customers.setup', customer.id)
        },
    },

    computed: {
        customersToEdit() {
            return this.displayedCustomers.filter(i => i.edit == true);
        },

        allSelected() {
            return this.displayedCustomers.length == this.customersToEdit.length;
        },
    },

    mounted() {
        this.setDateRange();
    },

    watch: {
        customers: {
            immediate: true,
            handler (newValue) {
                this.displayedCustomers = newValue.data.map(i => {
                    return {
                        id: i.id,
                        edit: false,
                    }
                });
            },
        },
    }
}
</script>

<style scoped>
.multi-custom {
    min-width: 8rem;
}

:deep(.dp-custom-input) {
    @apply h-7;
}
</style>